import type { StateCreator } from "zustand";
import { validateOpeningSet } from "../domain/openings/validateOpeningSet";
import { getT } from "../i18n";
import { formatOpeningIssue } from "../i18n/formatOpeningIssue";
import * as repo from "../lib/designsRepo";
import { AUTO_SAVE_DELAY_MS } from "../pages/builder/constants";
import { createFingerprint, toDesignPayload } from "../pages/builder/mappers";
import type { Design, SaunaType } from "../types/design";
import { useBuilderStore } from "./useBuilderStore";
import type { BuilderStore } from "./useBuilderStore";

let statusTimer: ReturnType<typeof setTimeout> | null = null;
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
let lastSavedFingerprint = "";
let autoSaveUnsub: (() => void) | null = null;

type StoreSet = Parameters<StateCreator<BuilderStore>>[0];
type StoreGet = Parameters<StateCreator<BuilderStore>>[1];
type BuilderFingerprintInput = Parameters<typeof createFingerprint>[0];

/**
 * Write persistence status (`saving`, `saveStatus`, `loadingDesign`) without
 * touching undo/redo history.
 *
 * `partialize` decides what a zundo snapshot CONTAINS, not whether one is
 * TAKEN: `temporalHandleSet` runs on every `set` (this store configures
 * neither `diff` nor `equality`), and `_handleSet` ends with
 * `futureStates: []`. So a bare `set({ saving: true })` silently DESTROYS the
 * redo branch. Auto-save fires ~2 s after every edit — including the edit an
 * undo just made — so without this guard "redo" stopped working two seconds
 * after every undo for any signed-in user with a design id.
 *
 * Only pure status writes belong in here. Anything that changes design data
 * (`hydrateFromDesign`, the draft actions) must stay tracked.
 */
export function setStatusWithoutHistory(write: () => void): void {
	const temporal = useBuilderStore.temporal.getState();
	const wasTracking = temporal.isTracking;
	if (!wasTracking) {
		write();
		return;
	}
	temporal.pause();
	try {
		write();
	} finally {
		temporal.resume();
	}
}

export interface AutoSaveConfig {
	getUserId: () => string | null;
	getDesignId: () => string | null;
	getType: () => SaunaType;
	setSearchParams: (
		nextInit: URLSearchParams | Record<string, string>,
		opts?: { replace?: boolean },
	) => void;
}

export function scheduleSaveStatusReset(
	set: StoreSet,
	delayMs: number,
): void {
	if (statusTimer) clearTimeout(statusTimer);
	statusTimer = setTimeout(() => {
		setStatusWithoutHistory(() => set({ saveStatus: null }));
		statusTimer = null;
	}, delayMs);
}

export function createPersistenceActions(
	set: StoreSet,
	get: StoreGet,
): Pick<
	BuilderStore,
	| "saveNow"
	| "_scheduleSaveStatusReset"
	| "_setSaving"
	| "_setSaveStatus"
	| "_setLoadingDesign"
> {
	return {
		saveNow: async ({ userId, designId, type, setSearchParams }) => {
			const t = getT(get().market);

			if (!userId) {
				setStatusWithoutHistory(() => set({ saveStatus: t.store_loginToSave }));
				return false;
			}

			const {
				market,
				locationMode,
				climateProfile,
					openings,
					partitionSplit,
					twoRoomConfig,
					timeOfDay,
					rotationDeg,
					wizardCapacityHint,
					roofConfig,
				buildProgress,
				dimensions,
				projectName,
				visibility,
				deckConfig,
				thermalInputs,
				siteConditions,
				interiorLayout,
				finishes,
				wallHeightM,
				indoorRoom,
				postalCode,
			} = get();
			const openingValidation = validateOpeningSet({
				openings,
				dimensions,
				wallHeightM: wallHeightM ?? undefined,
 partitionSplit, roofConfig, hasRelaxRoom:type === "tvarum" && twoRoomConfig.hasRelaxRoom, saunaPosition:twoRoomConfig.saunaPosition,
			});
			if (!openingValidation.valid) {
				const issue = openingValidation.issues[0];
				setStatusWithoutHistory(() =>
					set({
						saveStatus: issue
							? `${t.store_cannotSave}: ${formatOpeningIssue(issue, t)}`
							: t.store_cannotSaveInvalidOpenings,
					}),
				);
				return false;
			}

			try {
				setStatusWithoutHistory(() => set({ saving: true, saveStatus: null }));
				const payload = toDesignPayload({
					projectName,
					type,
					market,
					locationMode,
					climateProfile,
					postalCode,
					dimensions,
						partitionSplit,
						twoRoomConfig,
						timeOfDay,
						rotationDeg,
						wizardCapacityHint,
						visibility,
					openings,
					deckConfig,
					roofConfig,
					buildProgress,
					thermalInputs,
					siteConditions,
					interiorLayout,
					finishes,
					wallHeightM,
					indoorRoom,
				});

				if (designId) {
					await repo.updateDesign(designId, payload);
					setStatusWithoutHistory(() => set({ saveStatus: t.store_saved }));
				} else {
					const design = await repo.createDesign(payload);
					setSearchParams({ id: design.id }, { replace: true });
					setStatusWithoutHistory(() =>
						set({ saveStatus: t.store_createdAndSaved }),
					);
				}
				return true;
			} catch (err) {
				setStatusWithoutHistory(() =>
					set({
						saveStatus:
							err instanceof Error ? err.message : t.store_couldNotSave,
					}),
				);
				return false;
			} finally {
				setStatusWithoutHistory(() => set({ saving: false }));
				get()._scheduleSaveStatusReset(3000);
			}
		},
		_scheduleSaveStatusReset: (delayMs) =>
			scheduleSaveStatusReset(set, delayMs),
		// Pure status setters - never part of the design, never part of history.
		_setSaving: (saving) => setStatusWithoutHistory(() => set({ saving })),
		_setSaveStatus: (saveStatus) =>
			setStatusWithoutHistory(() => set({ saveStatus })),
		_setLoadingDesign: (loadingDesign) =>
			setStatusWithoutHistory(() => set({ loadingDesign })),
	};
}

export function startAutoSave(config: AutoSaveConfig): () => void {
	stopAutoSave();

	let savingRef = false;

	autoSaveUnsub = useBuilderStore.subscribe(
		(state) => ({
			projectName: state.projectName,
				buildProgress: state.buildProgress,
				dimensions: state.dimensions,
				partitionSplit: state.partitionSplit,
				twoRoomConfig: state.twoRoomConfig,
				timeOfDay: state.timeOfDay,
				rotationDeg: state.rotationDeg,
				wizardCapacityHint: state.wizardCapacityHint,
				visibility: state.visibility,
			openings: state.openings,
			deckConfig: state.deckConfig,
			roofConfig: state.roofConfig,
			thermalInputs: state.thermalInputs,
			siteConditions: state.siteConditions,
			interiorLayout: state.interiorLayout,
			finishes: state.finishes,
			wallHeightM: state.wallHeightM,
			indoorRoom: state.indoorRoom,
			market: state.market,
			locationMode: state.locationMode,
			climateProfile: state.climateProfile,
			postalCode: state.postalCode,
		}),
		() => {
			const userId = config.getUserId();
			const designId = config.getDesignId();
			if (!designId || !userId) return;

			const state = useBuilderStore.getState();
			const openingValidation = validateOpeningSet({
				openings: state.openings,
				dimensions: state.dimensions,
				wallHeightM: state.wallHeightM ?? undefined,
 partitionSplit:state.partitionSplit, roofConfig:state.roofConfig, hasRelaxRoom:config.getType() === "tvarum" && state.twoRoomConfig.hasRelaxRoom, saunaPosition:state.twoRoomConfig.saunaPosition,
			});
			if (!openingValidation.valid) {
				const t = getT(state.market);
				const issue = openingValidation.issues[0];
				setStatusWithoutHistory(() =>
					useBuilderStore.setState({
						saveStatus: issue
							? `${t.store_cannotAutoSave}: ${formatOpeningIssue(issue, t)}`
							: t.store_cannotAutoSaveInvalidOpenings,
					}),
				);
				return;
			}

			const fingerprint = createFingerprint(
				getBuilderFingerprintInput(config.getType(), state),
			);
			if (fingerprint === lastSavedFingerprint) return;

			if (autoSaveTimer) clearTimeout(autoSaveTimer);

			autoSaveTimer = setTimeout(async () => {
				if (savingRef) return;
				savingRef = true;
				setStatusWithoutHistory(() =>
					useBuilderStore.setState({ saving: true }),
				);

				const freshState = useBuilderStore.getState();
				const freshDesignId = config.getDesignId();

				if (!freshDesignId) {
					savingRef = false;
					setStatusWithoutHistory(() =>
						useBuilderStore.setState({ saving: false }),
					);
					return;
				}

				try {
					await repo.updateDesign(
						freshDesignId,
						toDesignPayload({
							projectName: freshState.projectName,
							type: config.getType(),
							market: freshState.market,
							locationMode: freshState.locationMode,
							climateProfile: freshState.climateProfile,
							postalCode: freshState.postalCode,
							dimensions: freshState.dimensions,
								partitionSplit: freshState.partitionSplit,
								twoRoomConfig: freshState.twoRoomConfig,
								timeOfDay: freshState.timeOfDay,
								rotationDeg: freshState.rotationDeg,
								wizardCapacityHint: freshState.wizardCapacityHint,
								visibility: freshState.visibility,
							openings: freshState.openings,
							deckConfig: freshState.deckConfig,
							roofConfig: freshState.roofConfig,
							buildProgress: freshState.buildProgress,
							thermalInputs: freshState.thermalInputs,
							siteConditions: freshState.siteConditions,
							interiorLayout: freshState.interiorLayout,
							finishes: freshState.finishes,
							wallHeightM: freshState.wallHeightM,
							indoorRoom: freshState.indoorRoom,
						}),
					);
					lastSavedFingerprint = createFingerprint(
						getBuilderFingerprintInput(config.getType(), freshState),
					);
					setStatusWithoutHistory(() =>
						useBuilderStore.setState({
							saveStatus: getT(freshState.market).store_autoSaved,
						}),
					);
				} catch {
					setStatusWithoutHistory(() =>
						useBuilderStore.setState({
							saveStatus: getT(freshState.market).store_autoSaveFailed,
						}),
					);
				} finally {
					savingRef = false;
					setStatusWithoutHistory(() =>
						useBuilderStore.setState({ saving: false }),
					);
					useBuilderStore.getState()._scheduleSaveStatusReset(2000);
				}
			}, AUTO_SAVE_DELAY_MS);
		},
		{ equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
	);

	return stopAutoSave;
}

function getBuilderFingerprintInput(
	type: SaunaType,
	state: BuilderStore,
): BuilderFingerprintInput {
	return {
		type,
		projectName: state.projectName,
		market: state.market,
		locationMode: state.locationMode,
		climateProfile: state.climateProfile,
		postalCode: state.postalCode,
		dimensions: state.dimensions,
			partitionSplit: state.partitionSplit,
			twoRoomConfig: state.twoRoomConfig,
			timeOfDay: state.timeOfDay,
			rotationDeg: state.rotationDeg,
			wizardCapacityHint: state.wizardCapacityHint,
			visibility: state.visibility,
		openings: state.openings,
		deckConfig: state.deckConfig,
		roofConfig: state.roofConfig,
		buildProgress: state.buildProgress,
		thermalInputs: state.thermalInputs,
		siteConditions: state.siteConditions,
		interiorLayout: state.interiorLayout,
		finishes: state.finishes,
		wallHeightM: state.wallHeightM,
		indoorRoom: state.indoorRoom,
	};
}

export function stopAutoSave(): void {
	if (autoSaveUnsub) {
		autoSaveUnsub();
		autoSaveUnsub = null;
	}
	if (autoSaveTimer) {
		clearTimeout(autoSaveTimer);
		autoSaveTimer = null;
	}
	lastSavedFingerprint = "";
}

/**
 * Returns the loaded design row (not just success) so the caller can
 * reconcile route-owned state — the URL's :type must be canonicalized to
 * the design's actual type before autosave may write it back.
 */
export async function loadDesign(designId: string): Promise<Design | null> {
	setStatusWithoutHistory(() =>
		useBuilderStore.setState({ loadingDesign: true }),
	);
	try {
		const design = await repo.getDesign(designId);
		if (design) {
			// hydrateFromDesign writes design data and stays tracked.
			useBuilderStore.getState().hydrateFromDesign(design);
			return design;
		}
		setStatusWithoutHistory(() =>
			useBuilderStore.setState({ loadingDesign: false }),
		);
		return null;
	} catch {
		setStatusWithoutHistory(() =>
			useBuilderStore.setState({ loadingDesign: false }),
		);
		return null;
	}
}
